import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { getAuth } from 'firebase/auth';

const SOCKET_URL = process.env.NODE_ENV === 'production' 
  ? 'https://your-production-domain.com' 
  : 'http://localhost:5000';

const RECONNECTION_ATTEMPTS = 5;
const RECONNECTION_DELAY = 1000;
const TYPING_DEBOUNCE = 1000;

export const useSocket = () => {
  const socketRef = useRef(null);
  const auth = getAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const typingTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const processingQueueRef = useRef(false);

  const processMessageQueue = useCallback(async () => {
    if (processingQueueRef.current || messageQueueRef.current.length === 0) return;

    processingQueueRef.current = true;
    const message = messageQueueRef.current[0];

    try {
      await sendMessage(message.content, message.roomId);
      messageQueueRef.current.shift();
    } catch (error) {
      console.error('Error processing message:', error);
      setError('Failed to send message');
    }

    processingQueueRef.current = false;
    if (messageQueueRef.current.length > 0) {
      processMessageQueue();
    }
  }, []);

  // sendMessage returns a Promise and handles socket ack
  const sendMessage = useCallback((content, roomId) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current?.connected) {
        messageQueueRef.current.push({ content, roomId });
        return resolve(); // resolve even if queued
      }
      socketRef.current.emit('send_message', { content, roomId }, (ack) => {
        if (ack?.error) return reject(new Error(ack.error));
        resolve();
      });
    });
  }, []);

  // connect uses onAuthStateChanged and unsubscribes on cleanup
  const connect = useCallback(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("User not authenticated");
        return;
      }
      // Always fetch a fresh token
      const token = await user.getIdToken();

      socketRef.current = io(SOCKET_URL, {
        auth: { token },
        reconnection: true,
        reconnectionAttempts: RECONNECTION_ATTEMPTS,
        reconnectionDelay: RECONNECTION_DELAY,
        timeout: 10000,
        transports: ['websocket', 'polling'],
      });

      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setError(null);
        if (messageQueueRef.current.length > 0) {
          processMessageQueue();
        }
      });

      socketRef.current.on('disconnect', () => setIsConnected(false));
      socketRef.current.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setError('Connection failed. Retrying...');
      });

      socketRef.current.on('error', (error) => {
        console.error('Socket error:', error);
        setError(error.message || 'An error occurred');
      });
    });

    return () => unsubscribe();
  }, [processMessageQueue]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      setIsConnected(false);
    }
  }, []);

  const joinRoom = useCallback((roomId) => {
    if (!socketRef.current?.connected) {
      console.warn('Socket not connected yet. Skipping joinRoom emit.');
      return;
    }
    socketRef.current.emit('join_room', roomId);
  }, []);

  const sendTypingStatus = useCallback((roomId, isTyping) => {
    if (!socketRef.current?.connected) return;

    clearTimeout(typingTimeoutRef.current);
    socketRef.current.emit('typing', { roomId, isTyping });

    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('typing', { roomId, isTyping: false });
      }, TYPING_DEBOUNCE);
    }
  }, []);

  const loadMoreMessages = useCallback((roomId, lastMessageId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('load_more_messages', { roomId, lastMessageId });
  }, []);

  const onMessage = useCallback((callback) => {
    if (!socketRef.current) return;
    socketRef.current.on('receive_message', callback);
    socketRef.current.on('initial_messages', callback);
    socketRef.current.on('more_messages', callback);
    return () => {
      socketRef.current.off('receive_message', callback);
      socketRef.current.off('initial_messages', callback);
      socketRef.current.off('more_messages', callback);
    };
  }, []);

  const onTyping = useCallback((callback) => {
    if (!socketRef.current) return;
    socketRef.current.on('user_typing', callback);
    return () => socketRef.current.off('user_typing', callback);
  }, []);

  const onError = useCallback((callback) => {
    if (!socketRef.current) return;
    socketRef.current.on('error', callback);
    return () => socketRef.current.off('error', callback);
  }, []);

  // useEffect cleans up unsubscribe
  useEffect(() => {
    const unsubscribe = connect();
    return () => {
      disconnect();
      clearTimeout(typingTimeoutRef.current);
      if (unsubscribe) unsubscribe();
    };
  }, [connect, disconnect]);

  return {
    connect,
    disconnect,
    joinRoom,
    sendMessage,
    sendTypingStatus,
    loadMoreMessages,
    onMessage,
    onTyping,
    onError,
    isConnected,
    error
  };
}; 